import * as React from 'react'
import { Link } from 'react-router'
import { Map as MapIcon } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SiteChangeDto, TreeWorkDto } from '@lib/dto/site-history.dto'
import type { SiteVisitDto } from '@lib/dto/site-visit.dto'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys, treeKeys } from '@/hooks/queryKeys'
import { StatusPill } from '@/components/StatusPill'
import { Button } from '@/components/ui/button'
import { describeChange, describeWork } from '@lib/helpers/describe-change'
import { formatVisitDay, visitCovers } from '@/site-visits/VisitBar'
import { toErrorMessage } from '@/helpers/logger'
import { takenBackRemovals } from '@lib/helpers/site-record'

/** "42 trees · 16 tree problems · 1 irrigation problem", or nothing found. */
function foundOn(visit: SiteVisitDto): string {
  const parts = [
    visit.coversTrees ? plural(visit.treesAssessed, 'tree') : null,
    visit.treeProblems > 0 ? plural(visit.treeProblems, 'tree problem') : null,
    visit.coversIrrigation
      ? plural(visit.irrigationProblems, 'irrigation problem')
      : null,
  ].filter((part): part is string => part !== null)
  return parts.length === 0 ? 'Nothing recorded' : parts.join(' · ')
}

function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? '' : 's'}`
}

/** One day of the timeline: the visit started on it, what changed, and work done. */
interface HistoryDay {
  day: string
  visits: SiteVisitDto[]
  changes: SiteChangeDto[]
  work: TreeWorkDto[]
}

/** Visits, changes and work by the day they happened, newest first. */
function byDay(
  visits: readonly SiteVisitDto[],
  changes: readonly SiteChangeDto[],
  work: readonly TreeWorkDto[],
): HistoryDay[] {
  const days = new Map<string, HistoryDay>()
  const dayOf = (day: string): HistoryDay => {
    const existing = days.get(day)
    if (existing !== undefined) return existing
    const created: HistoryDay = { day, visits: [], changes: [], work: [] }
    days.set(day, created)
    return created
  }
  for (const visit of visits) dayOf(visit.startedOn).visits.push(visit)
  for (const change of changes) dayOf(change.effectiveOn).changes.push(change)
  for (const done of work) dayOf(done.completedOn).work.push(done)
  return [...days.values()].sort((a, b) => b.day.localeCompare(a.day))
}

/**
 * Whether a change is a removal a job made that its record of work already
 * says: the work's line, which can be taken back, stands for both.
 */
function saidByWork(
  change: SiteChangeDto,
  work: readonly TreeWorkDto[],
): boolean {
  return (
    change.action === 'REMOVED' &&
    change.job !== null &&
    work.some(
      (done) =>
        done.removal &&
        done.treeId === change.entityId &&
        done.completedOn === change.effectiveOn,
    )
  )
}

function VisitRow({ siteId, visit }: { siteId: string; visit: SiteVisitDto }) {
  return (
    <li className="space-y-0.5 px-3 py-2">
      <p className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">Visit · {visitCovers(visit)}</span>
        <span className="flex items-center gap-2 text-xs text-muted-text">
          {visit.finishedAt === null ? 'Open' : 'Finished'}
          {/* The site as it stood that day, with what the visit found. */}
          <Link
            to={`/sites/${siteId}/visits/${visit.id}/map`}
            className="flex items-center gap-1 rounded px-1 text-secondary-text hover:bg-subtle hover:text-primary-text"
          >
            <MapIcon className="h-3.5 w-3.5" />
            Map
          </Link>
        </span>
      </p>
      <p className="text-xs text-secondary-text">
        {foundOn(visit)}
        {visit.startedByName === null ? '' : ` · ${visit.startedByName}`}
      </p>
    </li>
  )
}

function ChangeRow({
  change,
  names,
  isTakenBack,
}: {
  change: SiteChangeDto
  names: Readonly<Record<string, string>>
  /** A removal a later "put back" undid. */
  isTakenBack: boolean
}) {
  function renderPill(): React.ReactNode {
    if (isTakenBack) {
      return (
        <StatusPill tone="gray" className="shrink-0">
          Taken back
        </StatusPill>
      )
    }
    return change.isCorrection ? (
      <StatusPill tone="gray" className="shrink-0">
        Correction
      </StatusPill>
    ) : null
  }
  return (
    <li className="flex items-baseline justify-between gap-2 px-3 py-1.5 text-sm">
      <span
        className={
          change.isCorrection || isTakenBack ? 'text-secondary-text' : undefined
        }
      >
        {describeChange(change, names)}
      </span>
      {renderPill()}
    </li>
  )
}

/**
 * Work a Housecall Pro job did on a tree, with a way to take it back when it
 * was not done: a removal taken back puts the tree on the site again.
 */
function WorkRow({ siteId, work }: { siteId: string; work: TreeWorkDto }) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = React.useState(false)
  const undo = useMutation({
    mutationFn: () => siteVisitService.undoTreeWork(work.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: siteVisitKeys.history(siteId),
        }),
        // A removal taken back puts its tree on the map again.
        queryClient.invalidateQueries({ queryKey: treeKeys.site(siteId) }),
      ])
    },
  })

  function renderAction(): React.ReactNode {
    if (!confirming) {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2 text-xs"
          onClick={() => {
            setConfirming(true)
          }}
        >
          Undo
        </Button>
      )
    }
    return (
      <span className="flex shrink-0 items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={undo.isPending}
          onClick={() => {
            undo.mutate()
          }}
        >
          Not done
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={undo.isPending}
          onClick={() => {
            setConfirming(false)
          }}
        >
          Keep
        </Button>
      </span>
    )
  }

  return (
    <li className="space-y-0.5 px-3 py-1.5">
      <p className="flex items-center justify-between gap-2 text-sm">
        <span>{describeWork(work)}</span>
        {renderAction()}
      </p>
      <p className="text-xs text-secondary-text">
        Work done
        {work.jobNumber === null ? '' : ` · Job ${work.jobNumber}`}
        {confirming ? ' · Take this record back?' : ''}
      </p>
      {undo.isError ? (
        <p role="alert" className="text-xs text-danger">
          {toErrorMessage(undo.error)}
        </p>
      ) : null}
    </li>
  )
}

/**
 * A site's history: each visit, and the real changes to its record, by day,
 * newest first. Corrections (the record having been wrong, everything
 * recorded on a site's first look among them) are behind a switch, for the
 * office: they are how the record came to be right, not news about the site.
 * Work Housecall Pro jobs did on its trees is there too, each with Undo for
 * one that was not done. See docs/site-visits.md.
 */
export function SiteHistory({ siteId }: { siteId: string }) {
  const [showCorrections, setShowCorrections] = React.useState(false)
  const switchId = React.useId()
  const query = useQuery({
    queryKey: siteVisitKeys.history(siteId),
    queryFn: () => siteVisitService.history(siteId),
  })

  if (query.isPending) {
    return <p className="text-sm text-muted-text">Loading the history…</p>
  }
  if (query.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        The history could not be loaded. Close this and try again.
      </p>
    )
  }

  const { visits, changes, treeWork, names } = query.data
  // A removal a later put-back undid is shown with the corrections.
  const undone = takenBackRemovals(changes)
  const listed = changes.filter((change) => !saidByWork(change, treeWork))
  const isHidden = (change: SiteChangeDto): boolean =>
    change.isCorrection || undone.has(change.id)
  const corrections = listed.filter(isHidden).length
  const shown = showCorrections
    ? listed
    : listed.filter((change) => !isHidden(change))
  const days = byDay(visits, shown, treeWork)

  return (
    <div className="space-y-3">
      {corrections === 0 ? null : (
        <div className="flex items-center gap-2">
          <input
            id={switchId}
            type="checkbox"
            className="h-4 w-4"
            checked={showCorrections}
            onChange={(event) => {
              setShowCorrections(event.target.checked)
            }}
          />
          <label htmlFor={switchId} className="text-sm text-secondary-text">
            Show {plural(corrections, 'correction')}
          </label>
        </div>
      )}
      {days.length === 0 ? (
        <p className="text-sm text-muted-text">Nothing recorded yet.</p>
      ) : (
        <ol className="space-y-3">
          {days.map((day) => (
            <li key={day.day} className="space-y-1">
              <h3 className="text-xs font-medium text-secondary-text">
                {formatVisitDay(day.day)}
              </h3>
              <ul className="divide-y divide-default rounded-md border border-default">
                {day.visits.map((visit) => (
                  <VisitRow key={visit.id} siteId={siteId} visit={visit} />
                ))}
                {day.work.map((done) => (
                  <WorkRow key={done.id} siteId={siteId} work={done} />
                ))}
                {day.changes.map((change) => (
                  <ChangeRow
                    key={change.id}
                    change={change}
                    names={names}
                    isTakenBack={undone.has(change.id)}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
